import { useCallback, useEffect, useMemo, useState } from "react";

export function usePagination({
	items,
	defaultPageSize = 25,
	loading = false,
	canPaginate = true,
	endOfResults = true,
	totalCount = null,
	getNext,
	itemLabelSingular = "item",
	itemLabelPlural = "items"
}) {
	const safeItems = Array.isArray(items) ? items : [];
	const resolvedTotalCount = Number.isFinite(totalCount) ? totalCount : null;
	const totalItemCount = resolvedTotalCount ?? safeItems.length;

	const [pageSize, setPageSize] = useState(defaultPageSize);
	const [currentPage, setCurrentPage] = useState(1);
	const [pendingPage, setPendingPage] = useState(null);
	const [pendingLast, setPendingLast] = useState(false);

	const totalPages = useMemo(() => {
		const pages = Math.ceil(totalItemCount / pageSize);
		return Math.max(1, pages || 1);
	}, [pageSize, totalItemCount]);

	const paginationPage = useMemo(() => {
		return Math.min(Math.max(1, currentPage), totalPages);
	}, [currentPage, totalPages]);

	useEffect(() => {
		if (currentPage > totalPages) {
			setCurrentPage(totalPages);
		}
	}, [currentPage, totalPages]);

	const paginatedItems = useMemo(() => {
		const start = (paginationPage - 1) * pageSize;
		return safeItems.slice(start, start + pageSize);
	}, [paginationPage, pageSize, safeItems]);

	const pageStartIndex = useMemo(() => {
		if (!paginatedItems.length) {
			return 0;
		}
		return (paginationPage - 1) * pageSize + 1;
	}, [paginatedItems.length, paginationPage, pageSize]);

	const pageEndIndex = useMemo(() => {
		if (!paginatedItems.length) {
			return 0;
		}
		return (paginationPage - 1) * pageSize + paginatedItems.length;
	}, [paginatedItems.length, paginationPage, pageSize]);

	const totalKnownLabel = useMemo(() => {
		if (resolvedTotalCount !== null) {
			return `${resolvedTotalCount}`;
		}
		const suffix = !endOfResults && canPaginate ? "+" : "";
		return `${safeItems.length}${suffix}`;
	}, [canPaginate, endOfResults, resolvedTotalCount, safeItems.length]);

	const activeLabel = safeItems.length === 1 ? itemLabelSingular : itemLabelPlural;

	const handlePageChange = useCallback(
		(_, value) => {
			setPendingLast(false);
			setPendingPage(null);

			const requiredCount = (value - 1) * pageSize + 1;
			const beyondLoaded = requiredCount > safeItems.length;
			const aimingForEnd = value >= totalPages;

			if (aimingForEnd && !endOfResults) {
				setPendingLast(true);
				if (!loading && typeof getNext === "function") {
					getNext();
				}
				return;
			}

			if (beyondLoaded && !endOfResults) {
				setPendingPage(value);
				if (!loading && typeof getNext === "function") {
					getNext();
				}
				return;
			}

			setCurrentPage(Math.min(value, totalPages));
		},
		[endOfResults, getNext, loading, pageSize, safeItems.length, totalPages]
	);

	const handlePageSizeChange = useCallback((event) => {
		const nextSize = Number(event.target.value) || defaultPageSize;
		setPageSize(nextSize);
		setCurrentPage(1);
		setPendingPage(null);
		setPendingLast(false);
	}, [defaultPageSize]);

	useEffect(() => {
		if (!canPaginate || loading) {
			return;
		}

		if (pendingLast) {
			if (endOfResults) {
				setCurrentPage(Math.max(1, totalPages));
				setPendingLast(false);
			} else if (typeof getNext === "function") {
				getNext();
			}
		}
	}, [canPaginate, endOfResults, getNext, loading, pendingLast, totalPages]);

	useEffect(() => {
		if (!canPaginate || loading || pendingLast || pendingPage === null) {
			return;
		}

		const requiredCount = (pendingPage - 1) * pageSize + 1;
		if (safeItems.length >= requiredCount || endOfResults) {
			setCurrentPage(Math.min(pendingPage, totalPages));
			setPendingPage(null);
			return;
		}

		if (typeof getNext === "function") {
			getNext();
		}
	}, [canPaginate, endOfResults, getNext, loading, pageSize, pendingLast, pendingPage, safeItems.length, totalPages]);

	const pageSummaryText = useMemo(() => {
		if (loading && safeItems.length === 0) {
			return `Loading ${itemLabelPlural}...`;
		}

		if (safeItems.length === 0) {
			return `No ${itemLabelPlural} to display`;
		}

		if ((pendingLast || pendingPage !== null) && !endOfResults) {
			return `Fetching more ${itemLabelPlural}...`;
		}

		if (loading && paginatedItems.length === 0 && !endOfResults && canPaginate) {
			const start = (paginationPage - 1) * pageSize + 1;
			const end = start + pageSize - 1;
			return `Loading ${start}-${end}...`;
		}

		return `${pageStartIndex}-${pageEndIndex} of ${totalKnownLabel} ${activeLabel}`;
	}, [activeLabel, canPaginate, endOfResults, itemLabelPlural, loading, pageEndIndex, pageSize, pageStartIndex, paginatedItems.length, paginationPage, pendingLast, pendingPage, totalKnownLabel, safeItems.length]);

	const resetPagination = useCallback(() => {
		setCurrentPage(1);
		setPendingPage(null);
		setPendingLast(false);
	}, []);

	return {
		pageSize,
		setPageSize,
		currentPage,
		setCurrentPage,
		paginationPage,
		paginationCount: totalPages,
		paginatedItems,
		pageStartIndex,
		pageEndIndex,
		pageSummaryText,
		handlePageChange,
		handlePageSizeChange,
		resetPagination
	};
}
